import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Link,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowBack, AccessTime, Person, Share } from '@mui/icons-material';
import { Article } from '../types';
import { updateSEOTags } from '../utils/seoUtils';

// For now, we'll use static data. Later this could be from an API
const getArticleBySlug = async (slug: string): Promise<Article | null> => {
  // This would normally be an API call
  const articles = await import('../data/articles.json').then(module => module.default).catch(() => ({ articles: [] }));
  const foundArticle = articles.articles.find((article: any) => article.slug === slug);
  return foundArticle ? foundArticle as Article : null;
};

const ArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareNotification, setShareNotification] = useState<string | null>(null);
  const [currentAffiliateUrl, setCurrentAffiliateUrl] = useState<string | null>(null);
  const [selectedImages, setSelectedImages] = useState<{ [key: number]: number }>({});

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) {
        setError('Article not found');
        setLoading(false);
        return;
      }

      try {
        const articleData = await getArticleBySlug(slug);
        if (!articleData) {
          setError('Article not found');
          setLoading(false);
          return;
        }

        setArticle(articleData);
        
        // Update SEO tags
        updateSEOTags({
          title: articleData.seoTitle,
          description: articleData.seoDescription,
          keywords: articleData.seoKeywords,
          locationName: 'FindFunNow Articles',
        });

      } catch (error) {
        console.error('Error fetching article:', error);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  const handleShare = async (url?: string, title?: string) => {
    const shareUrl = url || window.location.href;
    const shareTitle = title || article?.title || 'Check this out!';
    
    if (navigator.share && isMobile) {
      try {
        await navigator.share({
          title: shareTitle,
          url: shareUrl,
        });
        setShareNotification('Shared successfully!');
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          await navigator.clipboard.writeText(shareUrl);
          setShareNotification('Link copied to clipboard!');
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShareNotification('Link copied to clipboard!');
      } catch (error) {
        setShareNotification('Failed to copy link');
      }
    }
  };

  // Function to extract booking URL from section data
  const getBookingUrl = (section: any) => {
    return section.affiliateUrl || 'https://www.vrbo.com';
  };

  const renderSection = (section: any, index: number) => {
    switch (section.type) {
      case 'header':
        const HeaderComponent = section.level === 1 ? 'h1' : section.level === 2 ? 'h2' : 'h3';
        const isNumberedTitle = section.level === 3 && /^\d+\./g.test(section.content);
        const bookingUrl = isNumberedTitle ? getBookingUrl(section) : null;
        
        // Set current affiliate URL for sharing when a numbered title is rendered
        if (isNumberedTitle && bookingUrl && !currentAffiliateUrl) {
          setCurrentAffiliateUrl(bookingUrl);
        }
        
        return (
          <Typography
            key={index}
            variant={section.level === 1 ? 'h3' : section.level === 2 ? 'h4' : 'h5'}
            component={HeaderComponent}
            sx={{
              fontWeight: 700,
              color: '#064db0ff',
              mb: section.level === 1 ? 3 : 2,
              mt: section.level === 1 ? 4 : 3,
              lineHeight: 1.3,
            }}
          >
            {isNumberedTitle && bookingUrl ? (
              <Link
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setCurrentAffiliateUrl(bookingUrl)}
                sx={{
                  textDecoration: 'underline',
                  color: '#0758caff',
                  '&:hover': {
                    color: '#10386fff',
                    textDecoration: 'underline',
                  },
                  cursor: 'pointer',
                }}
              >
                {section.content}
              </Link>
            ) : (
              section.content
            )}
          </Typography>
        );
      
      case 'subheader':
        return (
          <Typography
            key={index}
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 600,
              color: '#374151',
              mb: 2,
              mt: 3,
              lineHeight: 1.4,
            }}
          >
            {section.content}
          </Typography>
        );
      
      case 'paragraph':
        return (
          <Typography
            key={index}
            variant="body1"
            component="p"
            sx={{
              color: '#4b5563',
              lineHeight: 1.7,
              mb: 2,
              fontSize: '1.1rem',
            }}
          >
            {section.content}
          </Typography>
        );
      
      case 'capacity':
        return (
          <Typography
            key={index}
            variant="body1"
            component="p"
            sx={{
              color: '#303030ff',
              fontWeight: 600,
              lineHeight: 1.8,
              mb: 1,
              mt: -1,
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {section.content}
          </Typography>
        );
      
      case 'image':
        const images = section.images || [section.image]; // Support both single image and multiple images
        const selectedImageIndex = selectedImages[index] || 0;
        const currentImage = images[selectedImageIndex];
        
        return (
          <Box key={index} sx={{ my: 4, position: 'relative' }}>
            {/* Main Image */}
            <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
              <img
                src={currentImage?.url}
                alt={currentImage?.alt}
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 12,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                }}
              />
              <IconButton
                onClick={() => handleShare(currentAffiliateUrl || currentImage?.url, currentImage?.caption)}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#1f2937',
                  width: 36,
                  height: 36,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                <Share sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            
            {/* Thumbnails */}
            {images.length > 1 && (
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                mt: 2, 
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                {images.map((img: any, imgIndex: number) => (
                  <Box
                    key={imgIndex}
                    onClick={() => setSelectedImages(prev => ({ ...prev, [index]: imgIndex }))}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: selectedImageIndex === imgIndex ? '2px solid #6366f1' : '2px solid transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        border: '2px solid #6366f1',
                      }
                    }}
                  >
                    <img
                      src={img.url}
                      alt={img.alt}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
            
            {/* Caption */}
            {currentImage?.caption && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  mt: 1,
                  color: '#6b7280',
                  fontStyle: 'italic',
                }}
              >
                {currentImage.caption}
              </Typography>
            )}
          </Box>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" color="error" gutterBottom>
          {error || 'Article not found'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          The article you're looking for doesn't exist or has been moved.
        </Typography>
        <IconButton
          onClick={() => navigate('/')}
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': { backgroundColor: 'primary.dark' }
          }}
        >
          <ArrowBack />
        </IconButton>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
      {/* Hero Image */}
      <Box
        sx={{
          height: 400,
          backgroundImage: `url(${article.thumbnailImage.url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 100%)',
          }}
        />
      </Box>

      {/* Article Content */}
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, mt: -8 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 3,
            backgroundColor: 'white',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Article Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 800,
                color: '#1f2937',
                lineHeight: 1.2,
                mb: 3,
              }}
            >
              {article.title}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Person sx={{ fontSize: '1rem', color: '#6b7280' }} />
                <Typography variant="body2" color="text.secondary">
                  {article.author}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTime sx={{ fontSize: '1rem', color: '#6b7280' }} />
                <Typography variant="body2" color="text.secondary">
                  {article.readTime}
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                {new Date(article.publishedDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
            </Box>

            {/* Tags */}
            {article.tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                {article.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: '#6366f1',
                      color: '#6366f1',
                      '&:hover': {
                        backgroundColor: '#6366f1',
                        color: 'white',
                      }
                    }}
                  />
                ))}
              </Box>
            )}

            <Divider sx={{ mb: 4 }} />
          </Box>

          {/* Article Sections */}
          <Box sx={{ '& > *:first-of-type': { mt: 0 } }}>
            {article.sections.map((section, index) => renderSection(section, index))}
          </Box>

          {/* SEO Tags Footer */}
          <Box sx={{ mt: 8, pt: 4, borderTop: '1px solid #e5e7eb' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
              Related Topics
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {article.seoKeywords.map((keyword, index) => (
                <Chip
                  key={index}
                  label={keyword}
                  size="small"
                  sx={{
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    '&:hover': {
                      backgroundColor: '#e5e7eb',
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Paper>
      </Container>
      
      {/* Share Notification */}
      <Snackbar
        open={!!shareNotification}
        autoHideDuration={3000}
        onClose={() => setShareNotification(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShareNotification(null)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {shareNotification}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArticlePage;